/**
 * Objet de base d'une fiche pratique
 */
export interface Factsheet {
  /**
   * abspath de la fiche pratique
   */
  id?: string;

  /**
   * abspath de la fiche pratique
   */
  abspath?: string;

  /**
   * Couleur de la fiche pratique
   */
  color: string;
  /**
   * Titre de la fiche pratique
   */
  title: string;
  /**
   * Image de la fiche pratique
   */
  image: string;
  /**
   * Un tableau des objets contenus dans la fiche pratique, voir {@link FactsheetItem}
   */
  items: Array<FactsheetItems>;
}

export enum FactsheetItemTypes {
  TITLE,
  BULLET,
  TEXT,
  BUTTON,
  IMAGE,
  VIDEO
}

export enum FactsheetImageSubTypes {
  SVG,
  PNG,
  GIF,
  JPEG,
  LOTTIE_FILE
}

export type FactsheetItems =
  | FactsheetItemTitle
  | FactsheetItemBullet
  | FactsheetItemText
  | FactsheetItemButton
  | FactsheetItemImage
  | FactsheetItemVideo;

/**
 * Objet contenu dans une fiche pratique, en fonction de sa propriété `type`, voir les interfaces étendues :
 *
 * {@link FactsheetItemTitle}
 *
 * {@link FactsheetItemBullet}
 *
 * {@link FactsheetItemText}
 *
 * {@link FactsheetItemButton}
 *
 * {@link FactsheetItemImage}
 *
 * {@link FactsheetItemVideo}
 */
interface FactsheetItem {
  /**
   * Type de l'objet
   */
  type: FactsheetItemTypes;
}

/**
 * Objet de type [TITLE]{@link FactsheetItemTypes} d'une fiche pratique
 */
export interface FactsheetItemTitle extends FactsheetItem {
  /**
   * Type de l'objet
   */
  type: FactsheetItemTypes.TITLE;
  /**
   * Titre de l'objet
   */
  title: string;
}

/**
 * Objet de type [BULLET]{@link FactsheetItemTypes} d'une fiche pratique
 */
export interface FactsheetItemBullet extends FactsheetItem {
  /**
   * Type de l'objet
   */
  type: FactsheetItemTypes.BULLET;
  /**
   * Titre de l'objet
   */
  title?: string;
  /**
   * Texte de l'objet
   */
  text: string;
  /**
   * Image de l'objet
   */
  image: string;
  /**
   * Alignement de l'image
   */
  imageAlign: 'left' | 'right';
}

/**
 * Objet de type [TEXT]{@link FactsheetItemTypes} d'une fiche pratique
 */
export interface FactsheetItemText extends FactsheetItem {
  /**
   * Type de l'objet
   */
  type: FactsheetItemTypes.TEXT;
  /**
   * Titre le l'objet
   */
  title?: string;
  /**
   * Image de l'objet
   */
  image?: string;
  /**
   * Bouton de l'objet (objet avec son titre et son href)
   */
  button?: { label: string; href: string };
  /**
   * Texte de l'objet
   */
  text: string;
  /**
   * For multiple elements inside same Factsheet Item
   * Example: Text || Video || Image all together in single item
   */
  items?: Array<any>;
}

/**
 * Objet de type [BUTTON]{@link FactsheetItemTypes} d'une fiche pratique
 */
export interface FactsheetItemButton extends FactsheetItem {
  /**
   * Type de l'objet
   */
  type: FactsheetItemTypes.BUTTON;
  /**
   * Titre du bouton de l'objet
   */
  label: string;
  /**
   * href du bouton de l'objet
   */
  href: string;
}

/**
 * Objet de type [IMAGE]{@link FactsheetItemTypes} d'une fiche pratique
 */
export interface FactsheetItemImage extends FactsheetItem {
  /**
   * Type de l'object
   */
  type: FactsheetItemTypes.IMAGE;
  /**
   * Type de l'image (png, gif, lottie,...)
   */
  subType: FactsheetImageSubTypes;
  /**
   * url de l'image de l'objet
   */
  url: string;
}

/**
 * type object [VIDEO]{@link FactsheetItemTypes} a factsheet
 */
export interface FactsheetItemVideo extends FactsheetItem {
  /**
   * Object type
   */
  type: FactsheetItemTypes.VIDEO;
  /**
   * Object video type
   */
  videoType: 'youtube' | 'vimeo';
  /**
   * object video url
   */
  url: string;
}
